import { Inject, Injectable } from '@angular/core';
import { PermissionsModuleAccessLevel } from '@predictiveindex/client-permissions-lib';
import { merge } from 'lodash';
// This will be set to true based on the webpack build
import { PI } from '../constants-types/PI.model';
import { CURRENT_APP_TYPE, ModuleName } from '../core-data-model';
import { AppType } from '../shared/Enums';
import { ModuleUserAccessService } from '../system-services';
import { FeatureFlagsService } from '../system-services/FeatureFlags.service';
import { InitDataService } from '../system-services/InitData/InitData.service';
import { ModuleAccessService } from '../system-services/ModuleAccess/ModuleAccess.service';
declare let pendo: any;

@Injectable({
    providedIn: 'root'
})
export class PendoTracking {
    constructor(
        private features: FeatureFlagsService,
        private initData: InitDataService,
        private moduleAccess: ModuleAccessService,
        private moduleUserAccess: ModuleUserAccessService,
        @Inject(CURRENT_APP_TYPE) private appType: AppType
    ) {}

    static trackEvent(eventName: string, eventInfo: any) {
        if (PI.appSettings.pendoOn && (window as any).pendo !== undefined) {
            pendo.track(eventName, eventInfo);
        }
    }

    static triggerEvent(eventName: string) {
        if (PI.appSettings.pendoOn && (window as any).pendo !== undefined) {
            pendo.showGuideByName(eventName);
        }
    }

    /**
     * This tries to get the feature information before it registers with
     * Pendo so Pendo can have the feature info.
     *
     * @param productFeatureFlags
     * @param customAppType The custom app type. This can be helpful if in
     * pi-spa for example where the app type is set at the top level and then
     * needs to change because it is actually in a different product than the
     * entire application was initialized with.
     */
    async updatePendoMetadata(
        productFeatureFlags?: string,
        customAppType?: AppType
    ) {
        let featureInfo = '';
        try {
            await this.features.completionPromise;
            if (productFeatureFlags) {
                featureInfo =
                    this.features.featureFlagValues() +
                    ', ' +
                    productFeatureFlags;
            } else {
                featureInfo = this.features.featureFlagValues();
            }
        } finally {
            if (PI.appSettings.pendoOn && (window as any).pendo !== undefined) {
                const updatedPendoData = merge(this.initData.pendoMetadata, {
                    visitor: {
                        features: featureInfo,
                        currentProductModule: customAppType
                            ? customAppType
                            : this.appType,
                        isDesignSelfServe:
                            this.moduleAccess.isTeamDiscoverySelfServe,
                        designSoftwareVersion: this.getDesignVersion(),
                        userHasDesignAccess:
                            this.moduleUserAccess.hasBasicDesignPermissions(),
                        accountClassificationType:
                            this.moduleAccess.accountClassificationType,
                        isOrgAdmin:
                            this.moduleUserAccess.hasAccessLevelForAnyModule(
                                PermissionsModuleAccessLevel.AllCompanyAdmin
                            ),
                        // Note: For all PI2 products be sure not to specify Modulename2
                        // as that is not used in the permission endpiont being queried
                        design2AccessLevel:
                            PermissionsModuleAccessLevel[
                                this.moduleUserAccess.getUserAccessLevel(
                                    ModuleName.Design
                                )
                            ],
                        hire2AccessLevel:
                            PermissionsModuleAccessLevel[
                                this.moduleUserAccess.getUserAccessLevel(
                                    ModuleName.Hire
                                )
                            ],
                        inspire2AccessLevel:
                            PermissionsModuleAccessLevel[
                                this.moduleUserAccess.getUserAccessLevel(
                                    ModuleName.Inspire
                                )
                            ],
                        diagnose2AccessLevel:
                            PermissionsModuleAccessLevel[
                                this.moduleUserAccess.getUserAccessLevel(
                                    ModuleName.Diagnose
                                )
                            ]
                    }
                });
                pendo.updateOptions(updatedPendoData);
            }
        }
    }

    getDesignVersion(): string {
        if (!this.moduleAccess.hasDesign) {
            return ModuleName.Unknown;
        } else if (this.moduleAccess.hasDesign1) {
            return ModuleName.Design;
        } else {
            return ModuleName.Design2;
        }
    }
}
